/**
 * Inspections-specific preferences hook.
 *
 * Wraps useListingPreferences with:
 *  - The inspections legacy migrator (converts old Kendo cache shapes).
 *  - Column policy resolved from the current scope and iimEnabled flag.
 *  - The scope-appropriate localStorage cache key.
 *  - Default inspections filter values.
 *
 * Returns the same shape as useListingPreferences so the parent component
 * can use it directly to initialize useListingQueryState.
 */
import { useMemo } from 'react';
import { useListingPreferences } from '@hooks/listings';
import { INITIAL_INSPECTION_FILTERS } from '../types';
import { columnPolicyForScope, cacheKeyForScope } from '../adapters/inspectionsColumnPolicy';
import { createInspectionsLegacyMigrator } from '../adapters/inspectionsLegacyMigrator';
const DEFAULT_PAGE_SIZE = 25;
/**
 * Loads, migrates, and saves Inspections listing preferences.
 *
 * The returned `preferences` object (when non-null) provides:
 *  - pageSize, sort, filters, activeFilterKeys, visibleColumnKeys
 *
 * When `preferences` is null, the parent should use feature defaults
 * (INITIAL_INSPECTION_FILTERS, DEFAULT_ACTIVE_FILTER_KEYS, default sort).
 */
export function useInspectionsPreferences(options) {
    const { scope, iimEnabled } = options;
    const cacheKey = useMemo(() => cacheKeyForScope(scope), [scope]);
    const policy = useMemo(() => columnPolicyForScope(scope, iimEnabled), [scope, iimEnabled]);
    const legacyMigrator = useMemo(() => createInspectionsLegacyMigrator(scope), [scope]);
    return useListingPreferences({
        cacheKey,
        policy,
        defaultFilters: INITIAL_INSPECTION_FILTERS,
        defaultPageSize: DEFAULT_PAGE_SIZE,
        legacyMigrator
    });
}
